import type { SalesLookupVariant } from 'apis/sales-lookup';
import type { LogPriceFormValues } from './build-inquiry-payload';
import type { HeadlinePrice } from './headline';
import { variantLabel } from './variants';

// The visit basket: every product the customer showed interest in, with the
// per-item feedback captured so far. Pure data + pure operations; persistence
// and React wiring live in hooks/use-basket. The variant and headline are
// SNAPSHOTS taken when the item was added — the basket must render and submit
// without re-fetching each family, and a reference price is only meaningful
// as of the moment the rep quoted it.
export interface BasketItem {
  key: string;
  familyId: number;
  familyName: string;
  variant: SalesLookupVariant;
  headline: HeadlinePrice | null;
  values: LogPriceFormValues;
  addedAt: string; // ISO
}

export const BASKET_STORAGE_KEY = 'sales-lookup-basket-v1';

export function newBasketKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Replace the item with the same key, else append. Insertion order is the
// order the rep walked the client through the products — keep it.
export function upsertBasketItem(items: BasketItem[], item: BasketItem): BasketItem[] {
  const index = items.findIndex((existing) => existing.key === item.key);
  if (index === -1) return [...items, item];
  return items.map((existing, i) => (i === index ? item : existing));
}

export function removeBasketItem(items: BasketItem[], key: string): BasketItem[] {
  return items.filter((item) => item.key !== key);
}

// One basket line per variant: adding Sadia 1.1kg twice edits the first entry.
export function findBasketItemByVariant(items: BasketItem[], variantId: number): BasketItem | undefined {
  return items.find((item) => item.variant.id === variantId);
}

export function serializeBasket(items: BasketItem[]): string {
  return JSON.stringify(items);
}

// Tolerant of anything sessionStorage hands back (old shape, corruption,
// null): a bad payload is an empty basket, never a crash on the list screen.
export function parseBasket(raw: string | null | undefined): BasketItem[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isBasketItem);
  } catch {
    return [];
  }
}

function isBasketItem(value: unknown): value is BasketItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<BasketItem>;
  return (
    typeof item.key === 'string' &&
    typeof item.familyId === 'number' &&
    typeof item.familyName === 'string' &&
    !!item.variant &&
    typeof item.variant === 'object' &&
    typeof item.variant.id === 'number' &&
    !!item.values &&
    typeof item.values === 'object'
  );
}

// The one-line recap under an item on the basket screen: what was captured.
export function basketItemSummary(item: BasketItem): string {
  const { values, variant, headline } = item;
  const unit = headline?.unit ?? variant.packUnit ?? variant.itemUnit ?? variant.baseUnit;
  const parts: string[] = [];
  if (values.price.trim()) parts.push(`AED ${values.price.trim()} / ${unit}`);
  if (values.volume.trim()) parts.push(`${values.volume.trim()} / order`);
  if (values.currentSupplier) parts.push(`via ${values.currentSupplier.label.trim()}`);
  const closed = (['brand', 'origin', 'state', 'spec', 'packaging'] as const).filter(
    (dimension) => !values[`${dimension}Flexible` as const]
  );
  if (closed.length) parts.push(`fixed ${closed.join(', ')}`);
  return parts.length ? parts.join(' · ') : 'No details yet';
}

export function basketItemVariantLabel(item: BasketItem): string {
  return variantLabel(item.variant);
}
